import { AgentAvatar } from "@/components/molecules/agent-avatar";
import { Field } from "@/components/molecules/field";
import { FileUpload } from "@/components/molecules/file-upload/index";
import { VoicePicker } from "@/components/molecules/voice-picker";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import Env from "@/config/environment";
import {
  ADDRESS_REGISTERS,
  DEFAULT_PLAYBACK_RATE,
  PLAYBACK_RATES,
  TEMPERAMENTS,
  type AddressRegister,
  type Agent,
  type Temperament,
} from "@/features/agents/types";
import { useAgents } from "@/features/agents/hooks/use-agents";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useUser } from "@/features/auth/hooks/use-user";
import { selectRandomAvatar } from "@/features/agents/utils/avatar-selection";
import { useTranslation } from "@/features/utils/i18n";
import { useAudioPlayer } from "@/features/utils/use-audio";
import { useVoiceBackgrounds } from "@/features/voice-backgrounds/hooks/use-voice-backgrounds";
import {
  useVoiceProviders,
  useVoices,
} from "@/features/voices/hooks/use-voices";
import {
  hasTemperamentSample,
  useSamplePlayer,
} from "@/features/voices/hooks/use-sample-player";
import { CircleNotchIcon, PauseIcon, PlayIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";

interface AgentFormData extends Agent {
  _background_b64?: string; // Only when custom is selected
  _background_name?: string; // Only when custom is selected
}

interface AgentFormProps {
  data: Partial<Agent>;
  onChange: (data: Partial<AgentFormData>) => void;
}

export function AgentForm({ data, onChange }: AgentFormProps) {
  const { t } = useTranslation("agents");
  const { data: user } = useUser();
  // The debug tones are played into the call, so they are platform-staff only — same rule
  // as the raw audio and the CSV export on a session.
  const isSysadmin = user?.role === "SYSADMIN";
  const { data: voices = [] } = useVoices();
  const { data: usableProviders = [] } = useVoiceProviders();
  const { data: voiceBackgrounds = [] } = useVoiceBackgrounds();
  const currentClient = useCurrentClient();

  // ElevenLabs only: the other providers' voices are hidden from the picker (the
  // runtime still plays them for an agent that already has one). Only while
  // ElevenLabs is usable on this deployment, so the picker is never blank.
  const selectableVoices =
    usableProviders.length === 0 || usableProviders.includes("elevenlabs")
      ? voices.filter((voice) => voice.provider_name === "elevenlabs")
      : voices.filter((voice) => usableProviders.includes(voice.provider_name));

  const [backgroundAudioFile, setBackgroundAudioFile] = useState<File | null>(
    null,
  );
  const [showCustomUpload, setShowCustomUpload] = useState(false);

  const selectedVoice = voices.find((v) => v.id === data.voice);
  const temperament = data.temperament || "professional";
  const temperamentSample = useSamplePlayer();
  const isTemperamentPlaying =
    !!selectedVoice && temperamentSample.playingId === selectedVoice.id;
  const isTemperamentLoading =
    !!selectedVoice && temperamentSample.loadingId === selectedVoice.id;
  // An agent created before the picker was narrowed keeps its voice; it is shown
  // so the field is not blank, and goes away as soon as another one is picked.
  const legacyVoice =
    selectedVoice && !selectableVoices.includes(selectedVoice)
      ? selectedVoice
      : undefined;

  // Transfers go from one agent to another: with a single agent there is
  // nobody to receive one from, so the option is not offered.
  const { data: agents = [] } = useAgents();
  const hasOtherAgents = agents.some((agent) => agent.id !== data.id);
  const selectedBackground = voiceBackgrounds.find(
    (bg) => bg.id === data.background_audio,
  );

  const currentAvatar =
    data.avatar_id || selectRandomAvatar(selectedVoice?.gender);

  const handleRandomAvatar = () => {
    const newAvatarId = selectRandomAvatar(selectedVoice?.gender);
    onChange({ ...data, avatar_id: newAvatarId });
  };

  // Auto-update avatar when voice gender changes
  useEffect(() => {
    if (!data.voice || !data.avatar_id || !selectedVoice?.gender) return;

    const currentPrefix = data.avatar_id.charAt(0); // 'f' or 'm'
    const newPrefix = selectedVoice.gender.toLowerCase() === "male" ? "m" : "f";

    // If gender changed, generate new random avatar with correct prefix
    if (currentPrefix !== newPrefix) {
      handleRandomAvatar();
    }
  }, [data.voice, selectedVoice?.gender]);

  const { isPlaying: isBackgroundPlaying, toggle: toggleBackground } =
    useAudioPlayer(
      `background-${data.background_audio}`,
      selectedBackground?.path
        ? Env.server +
            "/api/files/v1/download?f=" +
            encodeURIComponent(selectedBackground.path)
        : undefined,
    );

  const handleBackgroundSelect = (value: string) => {
    if (value === "custom") {
      setShowCustomUpload(true);
      onChange({ ...data, background_audio: "" });
    } else if (value === "none") {
      setShowCustomUpload(false);
      setBackgroundAudioFile(null);
      onChange({ ...data, background_audio: "" });
    } else {
      setShowCustomUpload(false);
      setBackgroundAudioFile(null);
      onChange({ ...data, background_audio: value }); // value is the ID
    }
  };

  const toggleBackgroundPreview = () => {
    if (!selectedBackground?.path) return;
    toggleBackground();
  };

  const handleBackgroundAudioChange = (file: File | File[] | null) => {
    if (file && !Array.isArray(file)) {
      setBackgroundAudioFile(file);

      // Read file as base64
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(",")[1]; // Remove data:audio/...;base64, prefix

        // Store base64 and filename in the form data
        // The backend will handle the upload when saving
        onChange({
          ...data,
          background_audio: ":custom", // Special marker for custom upload
          _background_b64: base64,
          _background_name: file.name,
        });
      };
      reader.readAsDataURL(file);
    } else {
      setBackgroundAudioFile(null);
      onChange({ ...data, background_audio: "" });
    }
  };

  return (
    // Saving is the page's (a dialog's button, an edit page's save bar): Enter
    // in a field must not submit — without a handler that was a full reload,
    // and every edit with it.
    <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
      <Field label={t("agents.form.avatar")}>
        <div className="flex items-center gap-4">
          <AgentAvatar avatarId={currentAvatar} name={data.name} size="xl" />
          <Button type="button" variant="outline" onClick={handleRandomAvatar}>
            {t("agents.form.changePicture")}
          </Button>
        </div>
      </Field>

      <Field label={t("agents.form.name")} htmlFor="name">
        <Input
          id="name"
          value={data.name}
          onChange={(e) => onChange({ ...data, name: e.target.value })}
          required
          placeholder={t("agents.form.namePlaceholder")}
        />
      </Field>

      <Field label={t("agents.form.role")} htmlFor="role">
        <Input
          id="role"
          value={data.role}
          onChange={(e) => onChange({ ...data, role: e.target.value })}
          required
          placeholder={t("agents.form.rolePlaceholder")}
        />
      </Field>

      <Field
        label={t("agents.form.identity")}
        htmlFor="identity"
        hint={t("agents.form.identityHint")}
      >
        <Textarea
          id="identity"
          value={data.identity}
          onChange={(e) => onChange({ ...data, identity: e.target.value })}
          placeholder={t("agents.form.identityPlaceholder")}
          rows={6}
        />
      </Field>

      <Field
        label={t("agents.form.addressRegister")}
        htmlFor="address_register"
        hint={t("agents.form.addressRegisterHint")}
      >
        <Select
          value={data.address_register || "formal"}
          onValueChange={(value) =>
            onChange({ ...data, address_register: value as AddressRegister })
          }
        >
          <SelectTrigger id="address_register">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ADDRESS_REGISTERS.map((register) => (
              <SelectItem key={register.value} value={register.value}>
                {t(register.label)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <Field
        label={t("agents.form.temperament")}
        htmlFor="temperament"
        hint={t("agents.form.temperamentHint")}
      >
        <div className="flex gap-2">
          <Select
            value={temperament}
            onValueChange={(value) => {
              temperamentSample.stop();
              onChange({ ...data, temperament: value as Temperament });
            }}
          >
            <SelectTrigger id="temperament" className="flex-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEMPERAMENTS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {t(option.label)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasTemperamentSample(selectedVoice) && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() =>
                selectedVoice &&
                temperamentSample.toggle(selectedVoice, {
                  temperament,
                  language: currentClient?.client.default_language,
                })
              }
              aria-label={t(
                isTemperamentPlaying || isTemperamentLoading
                  ? "agents.form.temperamentPreviewStop"
                  : "agents.form.temperamentPreview",
              )}
              title={t("agents.form.temperamentPreview")}
            >
              {isTemperamentLoading ? (
                <CircleNotchIcon className="h-4 w-4 animate-spin" />
              ) : isTemperamentPlaying ? (
                <PauseIcon weight="fill" className="h-4 w-4" />
              ) : (
                <PlayIcon weight="fill" className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      </Field>

      <Field
        label={t("agents.form.defaultGreetings")}
        htmlFor="default_greetings"
        hint={t("agents.form.defaultGreetingsHint")}
      >
        <Textarea
          id="default_greetings"
          value={data.default_greetings}
          onChange={(e) =>
            onChange({ ...data, default_greetings: e.target.value })
          }
          placeholder={t("agents.form.defaultGreetingsPlaceholder")}
          rows={3}
        />
      </Field>

      <Field label={t("agents.form.voice")} htmlFor="voice">
        <VoicePicker
          id="voice"
          voices={selectableVoices}
          value={data.voice}
          unavailableVoice={legacyVoice}
          defaultLanguage={currentClient?.client.default_language}
          temperament={temperament}
          onChange={(voice) => onChange({ ...data, voice })}
        />
      </Field>

      <Field
        label={t("agents.form.backgroundAudio")}
        htmlFor="background_audio"
        hint={
          selectedBackground?.path &&
          !showCustomUpload &&
          t("agents.form.backgroundPreviewHint")
        }
      >
        <div className="flex gap-2">
          <Select
            value={
              showCustomUpload ? "custom" : data.background_audio || "none"
            }
            onValueChange={handleBackgroundSelect}
          >
            <SelectTrigger id="background_audio" className="flex-1">
              <SelectValue
                placeholder={t("agents.form.backgroundAudioPlaceholder")}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("agents.form.none")}</SelectItem>
              {voiceBackgrounds.map((bg) => (
                <SelectItem key={bg.id} value={bg.id}>
                  {bg.name}
                </SelectItem>
              ))}
              <SelectItem value="custom">
                {t("agents.form.customUpload")}
              </SelectItem>
            </SelectContent>
          </Select>
          {selectedBackground?.path && !showCustomUpload && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={toggleBackgroundPreview}
              disabled={!selectedBackground.path}
            >
              {isBackgroundPlaying ? (
                <PauseIcon weight="fill" className="h-4 w-4" />
              ) : (
                <PlayIcon weight="fill" className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
        {showCustomUpload && (
          <div className="mt-2">
            <FileUpload
              type="audio"
              accept="audio/*"
              value={backgroundAudioFile}
              onChange={handleBackgroundAudioChange}
              maxSize={10 * 1024 * 1024} // 10MB
            />
            <p className="text-xs text-muted-foreground mt-2">
              {t("agents.form.customUploadHint")}
            </p>
          </div>
        )}
      </Field>
      {hasOtherAgents && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id="can_receive_transfers"
            checked={data.can_receive_transfers || false}
            onCheckedChange={(checked) =>
              onChange({ ...data, can_receive_transfers: !!checked })
            }
          />
          <Label
            htmlFor="can_receive_transfers"
            className="text-sm font-normal cursor-pointer"
          >
            {t("agents.form.canReceiveTransfers")}
          </Label>
        </div>
      )}

      {isSysadmin && selectedVoice?.provider_name === "elevenlabs" && (
        <div className="flex items-start space-x-2">
          <Checkbox
            id="expressive_voice"
            className="mt-0.5"
            // On by default: only an explicit `false` opts out.
            checked={data.expressive_voice !== false}
            onCheckedChange={(checked) =>
              onChange({ ...data, expressive_voice: !!checked })
            }
          />
          <div className="grid gap-1">
            <Label
              htmlFor="expressive_voice"
              className="text-sm font-normal cursor-pointer"
            >
              {t("agents.form.expressiveVoice")}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t("agents.form.expressiveVoiceHint")}
            </p>
          </div>
        </div>
      )}

      {isSysadmin && (
        <Field
          label={t("agents.form.playbackRate")}
          htmlFor="playback_rate"
          hint={t("agents.form.playbackRateHint")}
        >
          <Select
            value={String(data.playback_rate ?? DEFAULT_PLAYBACK_RATE)}
            onValueChange={(value) =>
              onChange({ ...data, playback_rate: Number(value) })
            }
          >
            <SelectTrigger id="playback_rate">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PLAYBACK_RATES.map((rate) => (
                <SelectItem key={rate} value={String(rate)}>
                  {Math.round(rate * 100)} %
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}

      {isSysadmin && (
        <div className="flex items-start space-x-2">
          <Checkbox
            id="debug_tones"
            className="mt-0.5"
            checked={data.debug_tones || false}
            onCheckedChange={(checked) =>
              onChange({ ...data, debug_tones: !!checked })
            }
          />
          <div className="grid gap-1">
            <Label
              htmlFor="debug_tones"
              className="text-sm font-normal cursor-pointer"
            >
              {t("agents.form.debugTones")}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t("agents.form.debugTonesHint")}
            </p>
          </div>
        </div>
      )}

      {isSysadmin && (
        <div className="flex items-start space-x-2">
          <Checkbox
            id="anticipated_start"
            className="mt-0.5"
            checked={data.anticipated_start || false}
            onCheckedChange={(checked) =>
              onChange({ ...data, anticipated_start: !!checked })
            }
          />
          <div className="grid gap-1">
            <Label
              htmlFor="anticipated_start"
              className="text-sm font-normal cursor-pointer"
            >
              {t("agents.form.anticipatedStart")}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t("agents.form.anticipatedStartHint")}
            </p>
          </div>
        </div>
      )}
    </form>
  );
}
